import { store } from '@/common/root';
import jsonParseSafe from '@/common/utils/jsonParseSafe';
import applyColors from '@/common/utils/applyColors';
import checkTopLevelStorageAccess from '@/common/utils/checkTopLevelStorageAccess';
import { UserAccount } from '@schema';
import type { useAuthStore } from '@/common/store/pinia/auth';
import type { usePermissionsStore } from '@/common/store/pinia/permissions';

async function initSelfService({
  authStore,
  permissionsStore
}: {
  authStore: ReturnType<typeof useAuthStore>,
  permissionsStore: ReturnType<typeof usePermissionsStore>
}) {
  applyColors();
  await checkTopLevelStorageAccess(authStore.setStorageAccessState);

  /* Fetch and Set the current user in case there's one */
  const userCredentials = localStorage.getItem('user');
  const scopedToken = localStorage.getItem('scopedToken') || '';
  const scopedRoles = jsonParseSafe(localStorage.getItem('scopedRoles') || '[]') as string[];
  const domain = localStorage.getItem('domain') || '';
  const currentProject = localStorage.getItem('currentProject') || '';

  /*
   * NOTE (mpetrov): Following a login link for another domain (/login/<domainName>/...) must not
   * restore the previously signed-in session (VSTOR-136219).
   * domain_name picked from window.location.pathname as VueRouter yet not initialized at the moment
   */
  const loginLinkDomain = window.location.pathname.match(/\/login\/([^/?#]+)/)?.[1];
  const isCrossDomainLoginLink = !!loginLinkDomain && decodeURIComponent(loginLinkDomain) !== domain;

  if (userCredentials && !isCrossDomainLoginLink) {
    authStore.setCurrentUser(jsonParseSafe(userCredentials) as UserAccount || {});
  }

  if ((userCredentials || scopedToken) && !isCrossDomainLoginLink) {
    authStore.setProject(currentProject);
    authStore.setToken(scopedToken);
    authStore.setScopedRoles(scopedRoles);
    authStore.setDomain(domain);

    await authStore.checkAccessDisabled();
    if (authStore.accessDisabled) {
      return;
    }
    if (currentProject) {
      // check if the user still able to perform actions in the project
      await authStore.signInProject(currentProject);
    } else {
      await permissionsStore.fetchAuthMeta();
      await store.dispatch('socket/CONNECT');
    }
  } else if (isCrossDomainLoginLink) {
    localStorage.removeItem('user');
    localStorage.removeItem('scopedToken');
    localStorage.removeItem('scopedRoles');
    localStorage.removeItem('domain');
    localStorage.removeItem('currentProject');
  }
}

export default initSelfService;
